import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { AdvancedMarker, APIProvider, Map } from "@vis.gl/react-google-maps";
import useDebounce from "hooks/useDebounce";
import * as React from "react";
import {
  ParsedAddress,
  PlaceDetailsResponse,
  PlacePrediction,
  PlacesAutocompleteResponse,
} from "../types/googlePlaces";
import { parseGoogleAddressComponents } from "../utils/addressParser";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_LOCATION_API_KEY || "";
const PLACES_AUTOCOMPLETE_URL =
  "https://places.googleapis.com/v1/places:autocomplete";
const GEOCODE_URL = "https://maps.googleapis.com/maps/api/geocode/json";

// Default coordinates (Netherlands)
const DEFAULT_COORDINATES = { lat: 52.09192549999999, lng: 5.1229572 };

interface GooglePlacesAutocompleteProps {
  onAddressSelect: (address: ParsedAddress) => void;
  initialCoordinates?: { lat: number; lng: number };
  label?: string;
  error?: boolean;
  helperText?: string;
}

export const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> =
  ({
    onAddressSelect,
    initialCoordinates,
    label = "Search for address",
    error,
    helperText,
  }) => {
    const [inputValue, setInputValue] = React.useState("");
    const [options, setOptions] = React.useState<PlacePrediction[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [markerPosition, setMarkerPosition] = React.useState<{
      lat: number;
      lng: number;
    } | null>(initialCoordinates || null);
    const [mapCenter, setMapCenter] = React.useState<{
      lat: number;
      lng: number;
    } | null>(initialCoordinates || null);

    const debouncedInput = useDebounce(inputValue, 400);

    // Track the latest request to prevent race conditions
    const latestRequestRef = React.useRef<string>("");

    // Fetch autocomplete suggestions
    React.useEffect(() => {
      if (!debouncedInput || debouncedInput.length < 3) {
        setOptions([]);
        return;
      }

      // Mark this as the latest request
      const currentRequest = debouncedInput;
      latestRequestRef.current = currentRequest;

      const fetchSuggestions = async () => {
        setLoading(true);
        try {
          const response = await fetch(PLACES_AUTOCOMPLETE_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Goog-Api-Key": GOOGLE_API_KEY,
            },
            body: JSON.stringify({
              input: debouncedInput,
              languageCode: "en",
            }),
          });

          // Only update if this is still the latest request
          if (latestRequestRef.current !== currentRequest) {
            return;
          }

          if (response.ok) {
            const data: PlacesAutocompleteResponse = await response.json();
            // Only update options if we got results, don't clear existing options on empty response
            if (data.suggestions && data.suggestions.length > 0) {
              setOptions(data.suggestions);
              setOpen(true);
            }
          }
        } catch (err) {
          console.error("Error fetching place suggestions:", err);
          // Don't clear options on error
        } finally {
          if (latestRequestRef.current === currentRequest) {
            setLoading(false);
          }
        }
      };

      fetchSuggestions();
    }, [debouncedInput]);

    // Fetch place details when a place is selected
    const handlePlaceSelect = async (place: PlacePrediction | null) => {
      if (!place) return;

      const placeId = place.placePrediction.placeId;

      try {
        const response = await fetch(
          `https://places.googleapis.com/v1/places/${placeId}?fields=addressComponents,location,formattedAddress&languageCode=en`,
          {
            headers: {
              "X-Goog-Api-Key": GOOGLE_API_KEY,
            },
          }
        );

        if (response.ok) {
          const placeDetails: PlaceDetailsResponse = await response.json();

          // Parse address components
          const parsedAddress = parseGoogleAddressComponents(
            placeDetails.addressComponents || []
          );

          // Set coordinates for map - center when selecting from autocomplete
          if (placeDetails.location) {
            const coords = {
              lat: placeDetails.location.latitude,
              lng: placeDetails.location.longitude,
            };
            setMarkerPosition(coords);
            setMapCenter(coords);
            parsedAddress.coordinates = coords;
          }

          onAddressSelect(parsedAddress);
        }
      } catch (err) {
        console.error("Error fetching place details:", err);
      }
    };

    // Handle map click - reverse geocode and add to options
    const handleMapClick = async (event: {
      detail: { latLng: google.maps.LatLngLiteral | null };
    }) => {
      if (!event.detail.latLng) return;

      const lat = event.detail.latLng.lat;
      const lng = event.detail.latLng.lng;

      try {
        const response = await fetch(
          `${GEOCODE_URL}?latlng=${lat},${lng}&key=${GOOGLE_API_KEY}`
        );

        if (response.ok) {
          const data = await response.json();

          if (data.results && data.results.length > 0) {
            const result = data.results[0];

            // Convert Google Geocoding result to our PlacePrediction format
            const placePrediction: PlacePrediction = {
              placePrediction: {
                placeId: result.place_id,
                text: { text: result.formatted_address },
                structuredFormat: {
                  mainText: { text: result.formatted_address.split(",")[0] },
                  secondaryText: {
                    text: result.formatted_address
                      .split(",")
                      .slice(1)
                      .join(",")
                      .trim(),
                  },
                },
              },
            };

            // Update marker position for visual feedback (don't center map)
            setMarkerPosition({ lat, lng });

            // Set the input value and add as option
            setInputValue(result.formatted_address);
            setOptions([placePrediction]);
            setOpen(true);
          }
        }
      } catch (err) {
        console.error("Error reverse geocoding:", err);
      }
    };

    const defaultMapCenter = initialCoordinates || DEFAULT_COORDINATES;

    // Clear mapCenter after it's been applied so user can freely navigate
    React.useEffect(() => {
      if (mapCenter) {
        const timer = setTimeout(() => setMapCenter(null), 100);
        return () => clearTimeout(timer);
      }
    }, [mapCenter]);

    return (
      <APIProvider apiKey={GOOGLE_API_KEY}>
        <Box sx={{ width: "100%" }}>
          <Autocomplete
            fullWidth
            freeSolo
            open={open && (options.length > 0 || loading)}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            options={options}
            loading={loading}
            inputValue={inputValue}
            onInputChange={(_, newValue) => setInputValue(newValue)}
            onChange={(_, value) => {
              handlePlaceSelect(value as PlacePrediction | null);
              setOpen(false);
            }}
            filterOptions={(x) => x}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              return option.placePrediction?.text?.text || "";
            }}
            isOptionEqualToValue={(option, value) =>
              option.placePrediction?.placeId === value.placePrediction?.placeId
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                error={error}
                helperText={helperText}
                InputProps={{
                  ...params.InputProps,
                  sx: { backgroundColor: "#fff", borderRadius: "16px" },
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {loading && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.placePrediction?.placeId}>
                <LocationOnIcon sx={{ mr: 1, color: "text.secondary" }} />
                <Box>
                  <Typography variant="body1">
                    {option.placePrediction?.structuredFormat?.mainText?.text ||
                      option.placePrediction?.text?.text}
                  </Typography>
                  {option.placePrediction?.structuredFormat?.secondaryText && (
                    <Typography variant="body2" color="text.secondary">
                      {
                        option.placePrediction.structuredFormat.secondaryText
                          .text
                      }
                    </Typography>
                  )}
                </Box>
              </li>
            )}
          />

          {/* Map Display */}
          <Box
            sx={{
              mt: 2,
              height: 250,
              borderRadius: "16px",
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Map
              defaultCenter={defaultMapCenter}
              center={mapCenter || undefined}
              defaultZoom={markerPosition ? 15 : 7}
              mapId="organization-location-map"
              gestureHandling="cooperative"
              disableDefaultUI={false}
              onClick={handleMapClick}
              style={{ cursor: "pointer" }}
            >
              {markerPosition && <AdvancedMarker position={markerPosition} />}
            </Map>
          </Box>
        </Box>
      </APIProvider>
    );
  };

export default GooglePlacesAutocomplete;
